import React, { useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import AdminDateInput from "../AdminDateInput";
import AnalyticsStatHero from "./AnalyticsStatHero";
import { formatInr } from "../../utils/financeFormat";
import TableTotalAmount, { ANALYTICS_TABLE_TOTAL_ROW_CLASS } from "./TableTotalAmount";

const SOURCE_OPTIONS = [
  { id: "partyboss", label: "Partyboss" },
  { id: "banquet", label: "Banquet Hall" },
  { id: "restaurant", label: "Restaurant" },
];

const SECTIONS = [
  { key: "partyboss", label: "Partyboss", linesKey: "partybossLines", totalKey: "partybossTotalRupee" },
  { key: "banquet", label: "Banquet Hall", linesKey: "banquetLines", totalKey: "banquetTotalRupee" },
  { key: "restaurant", label: "Restaurant", linesKey: "restaurantLines", totalKey: "restaurantTotalRupee" },
];

function CashIncomeTable({ lines, total, saving, source, onDeleteLine }) {
  return (
    <Table striped bordered hover size="sm" variant="dark" responsive className="mb-0">
      <thead>
        <tr>
          <th>Date</th>
          <th className="text-end">Amount ₹</th>
          <th>Person</th>
          <th>Comments</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {(lines || []).length === 0 ? (
          <tr>
            <td colSpan={5} className="text-secondary small">
              No entries for this source yet.
            </td>
          </tr>
        ) : (
          <>
            {lines.map((line) => (
              <tr key={`ci-${source}-${line.id}`}>
                <td>{line.txnDateIso || "—"}</td>
                <td className="text-end text-success">{formatInr(line.amountRupee)}</td>
                <td>{line.person || "—"}</td>
                <td className="small">{line.comments || "—"}</td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={saving}
                    onClick={() => onDeleteLine?.(source, line.id)}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
            <tr className={ANALYTICS_TABLE_TOTAL_ROW_CLASS}>
              <td>Section total</td>
              <td className="text-end fs-6">
                <TableTotalAmount amount={total} toneClass="text-success" />
              </td>
              <td colSpan={3} />
            </tr>
          </>
        )}
      </tbody>
    </Table>
  );
}

export default function MonthlyCashIncomeTab({
  periodMonth,
  incomeDto,
  busy,
  onRefresh,
  onAddLine,
  onDeleteLine,
}) {
  const [source, setSource] = useState("partyboss");
  const [txnDate, setTxnDate] = useState("");
  const [amount, setAmount] = useState("");
  const [person, setPerson] = useState("");
  const [comments, setComments] = useState("");

  const total = incomeDto?.totalCashIncomeRupee ?? 0;

  const handleAdd = () => {
    const rupee = Number(amount) || 0;
    if (rupee <= 0 || !source) return;
    onAddLine?.({
      source,
      txnDateIso: txnDate,
      amountRupee: rupee,
      person: person.trim(),
      comments: comments.trim(),
    });
    setAmount("");
    setPerson("");
    setComments("");
  };

  return (
    <>
      <AnalyticsStatHero
        label={`Cash income total — ${periodMonth}`}
        amountRupee={total}
        valueClassName="text-success"
        meta={
          <div>
            Manual cash received — included in Profit as Money In → Cash In.
          </div>
        }
      />
      <Button size="sm" variant="outline-light" className="mb-3" onClick={onRefresh} disabled={busy}>
        Refresh cash income
      </Button>

      <div className="border border-secondary rounded p-3 mb-4">
        <h5 className="h6 text-white mb-3">Add cash income</h5>
        <div className="row g-2 align-items-end">
          <div className="col-md-2">
            <Form.Label className="text-secondary small">Date</Form.Label>
            <AdminDateInput
              size="sm"
              value={txnDate}
              onChange={(e) => setTxnDate(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <Form.Label className="text-secondary small">Source</Form.Label>
            <Form.Select
              size="sm"
              className="text-bg-dark border-secondary"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            >
              {SOURCE_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="col-md-2">
            <Form.Label className="text-secondary small">Amount ₹</Form.Label>
            <Form.Control
              size="sm"
              type="number"
              min={0}
              className="text-bg-dark border-secondary"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <Form.Label className="text-secondary small">Person</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              className="text-bg-dark border-secondary"
              value={person}
              onChange={(e) => setPerson(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <Form.Label className="text-secondary small">Comments</Form.Label>
            <Form.Control
              size="sm"
              type="text"
              className="text-bg-dark border-secondary"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>
          <div className="col-md-2">
            <Button size="sm" variant="success" disabled={busy || !(Number(amount) > 0)} onClick={handleAdd}>
              {busy ? "Saving…" : "Add entry"}
            </Button>
          </div>
        </div>
      </div>

      {SECTIONS.map((section) => (
        <div key={section.key} className="mb-4">
          <h5 className="h6 text-info mb-2">
            {section.label}
            <span className="float-end text-success">{formatInr(incomeDto?.[section.totalKey])}</span>
          </h5>
          <CashIncomeTable
            lines={incomeDto?.[section.linesKey]}
            total={incomeDto?.[section.totalKey]}
            saving={busy}
            source={section.key}
            onDeleteLine={onDeleteLine}
          />
        </div>
      ))}
    </>
  );
}
